import { useState, useMemo, useCallback, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { RenderIcon } from "./IconPickerPopover";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FloppyDisk,
  Trash,
  FilePdf,
} from "@phosphor-icons/react";
import {
  TierPricing,
  ModulePricing,
  RealEstateKey,
  RealEstatePricingModelMap,
  CategoryMultipliers,
  VolumeDiscounts,
  CustomerOffer,
  ModuleOverride,
  REAL_ESTATE_LABELS,
  EMPTY_UNITS,
} from "../types";

interface OfferBuilderProps {
  tierPricing: TierPricing;
  modulePricing: ModulePricing;
  rePricingModel: RealEstatePricingModelMap;
  categoryMultipliers: CategoryMultipliers;
  volumeDiscounts: VolumeDiscounts;
}

const fmt = (n: number) => "\u20AC" + Math.round(n).toLocaleString("nl-BE");
const reKeys = Object.keys(REAL_ESTATE_LABELS) as RealEstateKey[];

interface TimelineSegment {
  startMonth: number;
  endMonth: number | null;
  startDate: string;
  endDate: string | null;
  mrrPrice: number;
  discountPerMonth: number;
  totalPeriod: number;
  months: number;
  activeTrials: string[];
  yearlyDiscountPct: number;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: "Concept", color: "bg-gray-100 text-gray-700" },
  sent: { label: "Verstuurd", color: "bg-blue-100 text-blue-700" },
  accepted: { label: "Geaccepteerd", color: "bg-emerald-100 text-emerald-700" },
  declined: { label: "Afgewezen", color: "bg-red-100 text-red-700" },
};

export const OfferBuilder = ({
  tierPricing,
  modulePricing,
  rePricingModel,
  categoryMultipliers,
  volumeDiscounts,
}: OfferBuilderProps) => {
  const { session } = useSession();
  const tierKeys = Object.keys(tierPricing);
  const allModules = Object.keys(modulePricing).filter((k) => modulePricing[k].enabled);

  // Offers list
  const [offers, setOffers] = useState<CustomerOffer[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingOffer, setEditingOffer] = useState<CustomerOffer | null>(null);

  // New offer state
  const [customerName, setCustomerName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [kboNumber, setKboNumber] = useState("");
  const [registeredAddress, setRegisteredAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [tier, setTier] = useState(tierKeys[0] || "free");
  const [units, setUnits] = useState<Record<RealEstateKey, number>>({ ...EMPTY_UNITS });
  const [extraUsers, setExtraUsers] = useState(0);
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());
  const [moduleOverrides, setModuleOverrides] = useState<ModuleOverride[]>([]);
  /** Per-year discount: [{year: 1, pct: 15}, {year: 2, pct: 10}] */
  const [yearlyDiscounts, setYearlyDiscounts] = useState<{year: number; pct: number}[]>([
    { year: 1, pct: 0 },
  ]);
  const [goLiveDate, setGoLiveDate] = useState(new Date().toISOString().split("T")[0]);
  const [oneOffFee, setOneOffFee] = useState(0);
  /** Excluded functionality IDs per module: { moduleKey: Set<funcId> } */
  const [excludedFuncs, setExcludedFuncs] = useState<Record<string, Set<string>>>({});
  /** Per-offer value overrides for functionalities: { modKey: { funcId: "custom text" } } */
  const [funcValueOverrides, setFuncValueOverrides] = useState<Record<string, Record<string, string>>>({});
  const [status, setStatus] = useState<string>("draft");
  const [validUntilDate, setValidUntilDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().split("T")[0];
  });
  const [offerReference, setOfferReference] = useState(() => {
    const now = new Date();
    return `OFF-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
  });
  const [saving, setSaving] = useState(false);

  // Fetch offers
  const fetchOffers = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    try {
      const response = await fetchWithAuth(`${API_URL}/pricing-simulations/offers/all`, { method: "GET" }, session);
      if (response.ok) setOffers(await response.json());
    } catch (error) {
      console.error("Error fetching offers:", error);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  // Pre-select tier modules
  const handleSelectTier = (tierKey: string) => {
    setTier(tierKey);
    const config = tierPricing[tierKey];
    if (config) {
      setSelectedModules(new Set(config.includedModules || []));
    }
  };

  // Calculate pricing breakdown
  const priceBreakdown = useMemo(() => {
    const tierConfig = tierPricing[tier];
    const basePrice = tierConfig?.basePrice || 0;
    const goLive = new Date(goLiveDate || Date.now());

    // Per-module cost breakdown with sequential discount periods
    type DiscountPeriod = { discountPct: number; months?: number; note?: string; startMonth: number };
    const moduleBreakdown: { key: string; label: string; fullCost: number; periods: DiscountPeriod[]; hasPermanentDiscount: boolean }[] = [];
    for (const modKey of selectedModules) {
      const mod = modulePricing[modKey];
      if (!mod?.enabled) continue;
      // Collect all overrides for this module in order
      const modOverrides = moduleOverrides
        .filter((o) => o.moduleKey === modKey)
        .map((o) => ({
          // Backward compat: convert old priceOverride (absolute €/unit) to discountPct
          // priceOverride=0 → 100% discount (free), priceOverride>0 with known module → calculate %, otherwise 0
          discountPct: o.discountPct ?? (o.priceOverride !== undefined
            ? (o.priceOverride === 0 ? 100 : Math.max(0, Math.round(100 - (o.priceOverride / Math.max(mod.pricePerUnit || 1, 0.01)) * 100)))
            : 0),
          months: o.months ?? o.trialMonths,
          note: o.note,
        }));

      let fullCost = 0;
      for (const reKey of reKeys) {
        const qty = units[reKey] || 0;
        if (qty <= 0) continue;
        const isM2 = rePricingModel[reKey] === "m2";
        const catMult = categoryMultipliers[reKey] ?? 1;
        fullCost += (isM2 ? mod.pricePerM2 : mod.pricePerUnit) * catMult * qty;
      }

      // Build sequential periods with startMonth offsets
      const periods: DiscountPeriod[] = [];
      let offsetMonth = 0;
      for (const ov of modOverrides) {
        periods.push({ discountPct: ov.discountPct, months: ov.months, note: ov.note, startMonth: offsetMonth });
        if (ov.months) offsetMonth += ov.months; else break; // permanent = last period
      }
      const hasPermanentDiscount = modOverrides.some((o) => !o.months);

      moduleBreakdown.push({ key: modKey, label: mod.label, fullCost, periods, hasPermanentDiscount });
    }

    const totalFullModuleCost = moduleBreakdown.reduce((s, m) => s + m.fullCost, 0);
    const extraUserCost = Math.max(0, extraUsers - (tierConfig?.includedUsers || 1)) * (tierConfig?.pricePerExtraUser || 0);
    const grossMRR = basePrice + totalFullModuleCost + extraUserCost; // full price MRR (no discounts)

    // Helper: get the discount % for a module at a given month offset
    const getModuleDiscountAtMonth = (mb: typeof moduleBreakdown[0], monthOffset: number): number => {
      for (let i = mb.periods.length - 1; i >= 0; i--) {
        const p = mb.periods[i];
        if (monthOffset >= p.startMonth) {
          if (!p.months) return p.discountPct; // permanent
          if (monthOffset < p.startMonth + p.months) return p.discountPct;
          return 0; // past this period, no more discount
        }
      }
      return 0;
    };

    // Helper: get module cost at a given month
    const getModuleCostAtMonth = (mb: typeof moduleBreakdown[0], monthOffset: number): number => {
      const pct = getModuleDiscountAtMonth(mb, monthOffset);
      return mb.fullCost * (1 - pct / 100);
    };

    // Per-year pricing with yearly discounts + module discount periods
    const monthsInYear1 = Math.max(1, 12 - goLive.getMonth());
    const yearPricing = yearlyDiscounts.map((yd) => {
      const monthsInYear = yd.year === 1 ? monthsInYear1 : 12;
      const yearDiscount = yd.pct || 0;
      let totalRevenue = 0;
      const monthOff = yd.year === 1 ? (12 - monthsInYear) : (monthsInYear1 + (yd.year - 2) * 12);
      for (let m = 0; m < monthsInYear; m++) {
        const currentMonth = monthOff + m;
        let monthMRR = basePrice + extraUserCost;
        for (const mb of moduleBreakdown) monthMRR += getModuleCostAtMonth(mb, currentMonth);
        monthMRR *= (1 - yearDiscount / 100);
        totalRevenue += monthMRR;
      }
      const avgMRR = totalRevenue / monthsInYear;
      return { year: yd.year, discountPct: yearDiscount, months: monthsInYear, avgMRR, totalRevenue, fullPriceMRR: grossMRR * (1 - yearDiscount / 100) };
    });

    // ── Pricing timeline: date-range segments based on when discounts change ──
    const breakpointSet = new Set<number>([0]);
    for (const mb of moduleBreakdown) {
      for (const p of mb.periods) {
        breakpointSet.add(p.startMonth);
        if (p.months) breakpointSet.add(p.startMonth + p.months);
      }
    }
    // Also add yearly discount boundaries
    for (const yd of yearlyDiscounts) {
      if (yd.year === 1) {
        breakpointSet.add(0); // already there
        breakpointSet.add(monthsInYear1);
      } else {
        const startMonth = monthsInYear1 + (yd.year - 2) * 12;
        breakpointSet.add(startMonth);
        breakpointSet.add(startMonth + 12);
      }
    }
    // Helper: get the yearly discount % for a given month offset
    const getYearlyDiscountPct = (monthOffset: number): number => {
      for (const yd of yearlyDiscounts) {
        const yrStart = yd.year === 1 ? 0 : monthsInYear1 + (yd.year - 2) * 12;
        const yrEnd = yd.year === 1 ? monthsInYear1 : yrStart + 12;
        if (monthOffset >= yrStart && monthOffset < yrEnd) return yd.pct || 0;
      }
      return 0;
    };

    const hasPermanent = moduleBreakdown.some((m) => m.hasPermanentDiscount);
    const allBreaks = [...breakpointSet].sort((a, b) => a - b);

    const addMonthsFn = (d: Date, m: number) => {
      const r = new Date(d);
      r.setMonth(r.getMonth() + m);
      return r;
    };
    const fmtDate = (d: Date) => d.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });

    const pricingTimeline: TimelineSegment[] = [];
    for (let i = 0; i < allBreaks.length; i++) {
      const startM = allBreaks[i];
      const endM = i < allBreaks.length - 1 ? allBreaks[i + 1] : null;
      const months = endM !== null ? endM - startM : 0;
      if (endM !== null && months <= 0) continue;

      // Calculate MRR at this point using sequential discount periods
      let mrr = basePrice + extraUserCost;
      const activeDiscounts: string[] = [];
      for (const mb of moduleBreakdown) {
        const disc = getModuleDiscountAtMonth(mb, startM);
        mrr += mb.fullCost * (1 - disc / 100);
        if (disc > 0) activeDiscounts.push(`${mb.label} -${disc}%`);
      }
      const yrDisc = getYearlyDiscountPct(startM);
      mrr = mrr * (1 - yrDisc / 100);

      const discountPerMonth = grossMRR - mrr;
      const sDate = addMonthsFn(goLive, startM);
      const eDate = endM !== null ? addMonthsFn(goLive, endM) : null;

      pricingTimeline.push({
        startMonth: startM,
        endMonth: endM,
        startDate: fmtDate(sDate),
        endDate: eDate ? fmtDate(eDate) : null,
        mrrPrice: mrr,
        discountPerMonth,
        totalPeriod: endM !== null ? mrr * months : 0,
        months,
        activeTrials: activeDiscounts,
        yearlyDiscountPct: yrDisc,
      });
    }

    // Deduplicate: merge consecutive segments with the same MRR
    const mergedTimeline: TimelineSegment[] = [];
    for (const seg of pricingTimeline) {
      const prev = mergedTimeline[mergedTimeline.length - 1];
      if (prev && Math.abs(prev.mrrPrice - seg.mrrPrice) < 1 && prev.endMonth !== null && seg.endMonth !== null) {
        prev.endMonth = seg.endMonth;
        prev.endDate = seg.endDate;
        prev.months += seg.months;
        prev.totalPeriod = prev.mrrPrice * prev.months;
      } else {
        mergedTimeline.push({ ...seg });
      }
    }

    // Total discount savings
    const totalDiscountSavings = mergedTimeline.reduce((sum, seg) => sum + (seg.discountPerMonth > 1 ? seg.discountPerMonth * seg.months : 0), 0);

    return { basePrice, moduleBreakdown, totalFullModuleCost, extraUserCost, grossMRR, yearPricing, monthsInYear1, pricingTimeline: mergedTimeline, totalDiscountSavings, hasPermanentDiscount: hasPermanent };
  }, [units, tier, selectedModules, moduleOverrides, yearlyDiscounts, extraUsers, goLiveDate, tierPricing, modulePricing, rePricingModel, categoryMultipliers]);

  const calculatedMRR = priceBreakdown.pricingTimeline[0]?.mrrPrice || priceBreakdown.grossMRR;

  // Save offer
  const handleSave = async () => {
    if (!session || !customerName.trim()) {
      toast.warning("Vul een klantnaam in");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customerName: customerName.trim(),
        contactEmail,
        contactPhone: contactPerson,
        notes: JSON.stringify({ notes, kboNumber, registeredAddress, contactPerson }),
        status,
        offerData: {
          tier,
          units,
          extraUsers,
          selectedModules: [...selectedModules],
          moduleOverrides,
          yearlyDiscounts,
          excludedFuncs: Object.fromEntries(Object.entries(excludedFuncs).map(([k, v]) => [k, [...v]])),
          funcValueOverrides,
          oneOffSetupFee: oneOffFee,
          goLiveDate,
          validUntilDate,
          offerReference,
        },
        calculatedMRR: Math.round(calculatedMRR * 100) / 100,
        calculatedARR: Math.round(calculatedMRR * 12 * 100) / 100,
      };

      const url = editingOffer?.id
        ? `${API_URL}/pricing-simulations/offers/${editingOffer.id}`
        : `${API_URL}/pricing-simulations/offers`;

      const response = await fetchWithAuth(url, {
        method: editingOffer?.id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }, session);

      if (response.ok) {
        toast.success(editingOffer?.id ? "Offerte bijgewerkt" : "Offerte aangemaakt");
        if (!editingOffer?.id) resetForm();
        fetchOffers();
      } else {
        const err = await response.text();
        toast.error(`Fout: ${err.substring(0, 100)}`);
      }
    } catch (error) {
      console.error("Error saving offer:", error);
      toast.error("Fout bij opslaan offerte");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setEditingOffer(null);
    setCustomerName("");
    setContactPerson("");
    setContactEmail("");
    setKboNumber("");
    setRegisteredAddress("");
    setNotes("");
    setTier(tierKeys[0] || "free");
    setUnits({ ...EMPTY_UNITS });
    setExtraUsers(0);
    setSelectedModules(new Set());
    setModuleOverrides([]);
    setYearlyDiscounts([{ year: 1, pct: 0 }]);
    setGoLiveDate(new Date().toISOString().split("T")[0]);
    setOneOffFee(0);
    setExcludedFuncs({});
    setFuncValueOverrides({});
    setStatus("draft");
    const d = new Date(); d.setDate(d.getDate() + 30);
    setValidUntilDate(d.toISOString().split("T")[0]);
    const now = new Date();
    setOfferReference(`OFF-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`);
  };

  const loadOffer = (offer: CustomerOffer) => {
    setEditingOffer(offer);
    setCustomerName(offer.customerName);
    setContactEmail(offer.contactEmail || "");
    setContactPerson(offer.contactPhone || ""); // stored in contactPhone field
    // notes field stores JSON with extra fields
    try {
      const parsed = JSON.parse(offer.notes || "{}");
      setNotes(parsed.notes || "");
      setKboNumber(parsed.kboNumber || "");
      setRegisteredAddress(parsed.registeredAddress || "");
      if (parsed.contactPerson) setContactPerson(parsed.contactPerson);
    } catch {
      setNotes(offer.notes || "");
    }
    setStatus(offer.status);
    const data = (offer as any).offerData as any;
    if (data) {
      setTier(data.tier || tierKeys[0]);
      setUnits(data.units || { ...EMPTY_UNITS });
      setExtraUsers(data.extraUsers || 0);
      setSelectedModules(new Set(data.selectedModules || []));
      setModuleOverrides(data.moduleOverrides || []);
      setYearlyDiscounts(data.yearlyDiscounts || [{ year: 1, pct: 0 }]);
      setOneOffFee(data.oneOffSetupFee || 0);
      if (data.excludedFuncs) {
        setExcludedFuncs(Object.fromEntries(Object.entries(data.excludedFuncs).map(([k, v]) => [k, new Set(v as string[])])));
      }
      if (data.funcValueOverrides) setFuncValueOverrides(data.funcValueOverrides);
      if (data.goLiveDate) setGoLiveDate(data.goLiveDate);
      if (data.validUntilDate) setValidUntilDate(data.validUntilDate);
      if (data.offerReference) setOfferReference(data.offerReference);
    }
  };

  const deleteOffer = async (id: number) => {
    if (!session) return;
    try {
      await fetchWithAuth(`${API_URL}/pricing-simulations/offers/${id}`, { method: "DELETE" }, session);
      toast.success("Offerte verwijderd");
      if (editingOffer?.id === id) resetForm();
      fetchOffers();
    } catch (error) {
      toast.error("Fout bij verwijderen");
    }
  };

  const addModuleOverride = (moduleKey: string) => {
    setModuleOverrides([...moduleOverrides, { moduleKey, discountPct: 100, months: 3, note: "Try-out" }]);
  };

  const toggleFuncExclusion = (modKey: string, funcId: string) => {
    setExcludedFuncs((prev) => {
      const current = new Set(prev[modKey] || []);
      if (current.has(funcId)) current.delete(funcId); else current.add(funcId);
      return { ...prev, [modKey]: current };
    });
  };

  const removeModuleOverride = (moduleKey: string) => {
    setModuleOverrides(moduleOverrides.filter((o) => o.moduleKey !== moduleKey));
  };

  const removeOverrideByIndex = (idx: number) => {
    setModuleOverrides(moduleOverrides.filter((_, i) => i !== idx));
  };

  const updateOverrideByIndex = (idx: number, patch: Partial<ModuleOverride>) => {
    setModuleOverrides(moduleOverrides.map((o, i) => i === idx ? { ...o, ...patch } : o));
  };

  const handleExportPdf = () => {
    const tierLabel = tierPricing[tier]?.label || "Op maat";
    const today = new Date().toLocaleDateString("nl-BE", { day: "numeric", month: "long", year: "numeric" });
    const goLiveFmt = new Date(goLiveDate).toLocaleDateString("nl-BE", { day: "numeric", month: "long", year: "numeric" });
    const validUntilFmt = new Date(validUntilDate).toLocaleDateString("nl-BE", { day: "numeric", month: "long", year: "numeric" });
    const portfolioItems = reKeys.filter((k) => (units[k] || 0) > 0);
    const hasTrials = moduleOverrides.length > 0;
    const hasYearlyDisc = yearlyDiscounts.some((yd) => yd.pct > 0);

    const win = window.open("", "_blank");
    if (!win) return;
    const grossMRR = priceBreakdown.grossMRR;
    const modRows = priceBreakdown.moduleBreakdown.filter((m) => m.fullCost > 0 || m.periods.length > 0);
    const portHtml = portfolioItems.map((k) => {
      const info = REAL_ESTATE_LABELS[k]; const isM2 = rePricingModel[k] === "m2";
      return `<div class="pill">${info.icon} <b>${units[k].toLocaleString("nl-BE")}</b> ${isM2 ? "m\u00B2" : "units"} ${info.label}</div>`;
    }).join("");
    const orderedSelected = allModules.filter((k) => selectedModules.has(k));
    const modGridHtml = orderedSelected.map((mk) => {
      const m = modulePricing[mk]; if (!m) return "";
      const ovs = moduleOverrides.filter((o) => o.moduleKey === mk);
      const discTag = ovs.length > 0 ? ovs.map((o) => {
        const pct = o.discountPct ?? (o.priceOverride === 0 ? 100 : 0);
        const dur = o.months ?? o.trialMonths;
        return `${pct === 100 ? "Gratis" : `-${pct}%`}${dur ? ` ${dur}m` : ""}`;
      }).join(", ") : "";
      return `<div class="mgrid-item"><span class="mdot"></span><b>${m.label}</b>${m.description ? `<span class="mdesc">${m.description.substring(0, 45)}</span>` : ""}${discTag ? `<span class="mtrial">${discTag}</span>` : ""}</div>`;
    }).join("");

    win.document.write(`<!DOCTYPE html><html><head><title>Offerte - ${customerName}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  :root{--pri:#2563eb;--pri-dark:#1e3a8a;--green:#059669;--amber:#d97706;--slate:#475569;--light:#f8fafc;--border:#e2e8f0;--bg2:#f1f5f9}
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'Inter',system-ui,sans-serif;color:#0f172a;background:#fff;font-size:13px;line-height:1.55}
  .page{max-width:780px;margin:0 auto;padding:40px 44px 28px}

  .hdr{display:flex;justify-content:space-between;align-items:center;margin-bottom:32px}
  .logo{font-size:28px;font-weight:800;color:var(--pri);letter-spacing:-0.04em}
  .logo span{color:var(--pri-dark)}
  .hdr-r{text-align:right;font-size:11px;color:var(--slate)}
  .hdr-r b{display:block;font-size:14px;color:#0f172a}

  .hero{background:linear-gradient(135deg,#2563eb 0%,#1e3a8a 100%);color:#fff;border-radius:16px;padding:32px 36px;margin-bottom:28px;position:relative;overflow:hidden}
  .hero::after{content:'';position:absolute;top:-40px;right:-40px;width:160px;height:160px;border-radius:50%;background:rgba(255,255,255,0.06)}
  .hero-ey{font-size:10px;text-transform:uppercase;letter-spacing:0.1em;opacity:0.6;margin-bottom:4px}
  .hero-name{font-size:28px;font-weight:800;letter-spacing:-0.02em;margin-bottom:16px}
  .hero-row{display:flex;gap:28px;align-items:flex-end;flex-wrap:wrap}
  .hero-big{font-size:42px;font-weight:800;letter-spacing:-0.04em;line-height:1}
  .hero-big sub{font-size:16px;font-weight:400;opacity:0.6;vertical-align:baseline}
  .hero-info{font-size:12px;opacity:0.7;line-height:1.6}
  .hero-pill{display:inline-block;background:rgba(255,255,255,0.15);border-radius:20px;padding:3px 12px;font-size:10px;font-weight:600;margin-top:4px}

  .sec{margin-bottom:24px}
  .sec-t{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:var(--pri);margin-bottom:8px}
  .sec-t::after{content:'';display:block;width:40px;height:2px;background:var(--pri);margin-top:4px;border-radius:1px}

  .pills{display:flex;flex-wrap:wrap;gap:6px}
  .pill{background:var(--light);border:1px solid var(--border);border-radius:8px;padding:8px 14px;font-size:12px}
  .pill b{font-weight:700}

  table{width:100%;border-collapse:collapse;font-size:12px;margin-top:4px}
  th{text-align:left;padding:8px 10px;color:var(--slate);font-weight:600;font-size:10px;text-transform:uppercase;letter-spacing:0.04em;border-bottom:2px solid var(--pri)}
  td{padding:9px 10px;border-bottom:1px solid var(--bg2)}
  td:last-child,th:last-child{text-align:right}
  .subtotal td{background:var(--light);font-weight:700;font-size:14px;border-bottom:none}
  .subtotal .amt{color:var(--pri);font-size:18px}
  .disc-row td{color:var(--green);font-weight:600}
  .disc-row td:last-child{font-size:14px}
  .final td{background:var(--pri);color:#fff;font-weight:700;font-size:15px;border-radius:0 0 8px 8px;border-bottom:none;padding:12px 10px}
  .trial-tag{display:inline-block;font-size:9px;color:var(--amber);background:#fffbeb;border:1px solid #fde68a;padding:0 6px;border-radius:10px;margin-left:6px;font-weight:600}
  .arr{color:var(--slate);font-weight:400;font-size:11px;margin-left:8px}

  .mgrid{display:grid;grid-template-columns:1fr 1fr;gap:5px}
  .mgrid-item{display:flex;align-items:center;gap:6px;font-size:11px;padding:6px 10px;background:var(--light);border-radius:6px;border:1px solid var(--bg2)}
  .mdot{width:6px;height:6px;border-radius:50%;background:var(--pri);flex-shrink:0}
  .mdesc{font-size:9px;color:var(--slate);margin-left:auto;max-width:160px;text-align:right}
  .mtrial{font-size:9px;color:var(--amber);font-weight:600;margin-left:auto}

  .notes{background:#fefce8;border:1px solid #fde68a;border-radius:10px;padding:14px 18px;font-size:12px;color:#854d0e;margin-top:20px}

  .ftr{margin-top:32px;padding-top:10px;border-top:1px solid var(--border);display:flex;justify-content:space-between;font-size:9px;color:#94a3b8}
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  @media print{.page{padding:20px 28px}.hero{padding:24px 28px}}
</style></head><body>
<div class="page">

  <div class="hdr">
    <div><div class="logo">Yally</div></div>
    <div class="hdr-r"><b>Offerte</b><br>${offerReference}<br>${today}${contactEmail ? `<br>${contactEmail}` : ""}</div>
  </div>

  <div class="hero">
    <div class="hero-ey">Offerte voor</div>
    <div class="hero-name">${customerName}</div>
    <div class="hero-row">
      <div><div class="hero-big">\u20AC${Math.round(priceBreakdown.pricingTimeline[0]?.mrrPrice || grossMRR).toLocaleString("nl-BE")}<sub>/maand</sub></div></div>
      <div class="hero-info">
        ${(hasTrials || hasYearlyDisc) ? `Uw startprijs (excl. btw)` : `Maandprijs (excl. btw)`}<br>
        Startdatum: ${goLiveFmt}
        ${(hasTrials || hasYearlyDisc) ? `<div class="hero-pill">\u20AC${Math.round(grossMRR).toLocaleString("nl-BE")}/mnd na kortingsperiode</div>` : ""}
      </div>
    </div>
  </div>

  <!-- Customer details -->
  <div class="sec">
    <div class="sec-t">Klantgegevens</div>
    <table>
      <tbody>
        <tr><td style="width:180px;color:var(--slate)">Bedrijf</td><td><b>${customerName}</b></td></tr>
        ${contactPerson ? `<tr><td style="color:var(--slate)">Contactpersoon</td><td>${contactPerson}</td></tr>` : ""}
        ${contactEmail ? `<tr><td style="color:var(--slate)">E-mail</td><td>${contactEmail}</td></tr>` : ""}
        ${kboNumber ? `<tr><td style="color:var(--slate)">KBO-nummer</td><td>${kboNumber}</td></tr>` : ""}
        ${registeredAddress ? `<tr><td style="color:var(--slate)">Maatschappelijke zetel</td><td>${registeredAddress}</td></tr>` : ""}
      </tbody>
    </table>
  </div>

  <!-- 1. Portfolio -->
  <div class="sec">
    <div class="sec-t">Uw portefeuille</div>
    <div class="pills">
      ${portHtml}
      ${extraUsers > 0 ? `<div class="pill">\u{1F464} <b>${extraUsers}</b> extra gebruikers</div>` : ""}
    </div>
  </div>

  <!-- 2. Included modules -->
  <div class="sec">
    <div class="sec-t">Inbegrepen in uw pakket (${orderedSelected.length} modules)</div>
    <div class="mgrid">${modGridHtml}</div>
  </div>

  <!-- 3. Functionalities detail -->
  ${orderedSelected.filter((k) => modulePricing[k]?.functionalities?.length).map((modKey) => {
    const mod2 = modulePricing[modKey];
    const funcs2 = mod2.functionalities || [];
    const excluded2 = excludedFuncs[modKey] || new Set();
    return `
    <div class="sec">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
        <span style="width:8px;height:8px;border-radius:50%;background:var(--pri);display:inline-block;flex-shrink:0"></span>
        <span style="font-weight:700;font-size:14px">${mod2.label}</span>
      </div>
      <table>
        <thead><tr><th style="text-align:left">Functionaliteit</th><th style="text-align:right;width:120px"></th></tr></thead>
        <tbody>
          ${funcs2.map((f) => {
            const isExcl2 = excluded2.has(f.id);
            const overrideV2 = funcValueOverrides[modKey]?.[f.id];
            const rawVal2 = overrideV2 !== undefined ? overrideV2 : (f.defaultValue || "");
            const textVal2 = (rawVal2 === "\u2714" || rawVal2 === "\u2713" || rawVal2 === "v" || rawVal2 === "V") ? "" : rawVal2.trim();
            const hasText2 = textVal2.length > 0;
            // Included + no text → ✔, Included + text → show text, Excluded + text → show text grayed, Excluded + no text → —
            const valueCell = isExcl2
              ? (hasText2 ? `<span style="color:#94a3b8">${textVal2}</span>` : `<span style="color:#94a3b8">&mdash;</span>`)
              : (hasText2 ? textVal2 : `\u2714`);
            return `<tr${isExcl2 ? ` style="color:#94a3b8"` : ""}>
              <td>${f.label}</td>
              <td style="text-align:right;font-weight:500">${valueCell}</td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>
    </div>`;
  }).join("")}

  <!-- 4. Full price breakdown (build up) -->
  <div class="sec">
    <div class="sec-t">Uw maandelijkse prijs</div>
    <table>
      <thead><tr><th>Onderdeel</th><th style="text-align:center">Berekening</th><th>Per maand</th></tr></thead>
      <tbody>
        <tr><td>Platformlicentie (${tierLabel})</td><td style="text-align:center;color:var(--slate);font-size:10px">vast bedrag</td><td>\u20AC${priceBreakdown.basePrice.toLocaleString("nl-BE")}</td></tr>
        ${modRows.map((m) => {
          // Build rate description from portfolio
          const mod = modulePricing[m.key];
          const rateDetails = portfolioItems.map((k) => {
            const qty = units[k]; const isM2 = rePricingModel[k] === "m2";
            const catMult = categoryMultipliers[k] ?? 1;
            const rate = isM2 ? (mod?.pricePerM2 || 0) * catMult : (mod?.pricePerUnit || 0) * catMult;
            return `\u20AC${rate.toFixed(isM2 ? 3 : 2)} \u00D7 ${qty.toLocaleString("nl-BE")} ${isM2 ? "m\u00B2" : "u"}`;
          }).join(" + ");
          const fp = m.periods[0];
          const hasDisc = fp && fp.discountPct > 0;
          const currentCost = hasDisc ? m.fullCost * (1 - fp.discountPct / 100) : m.fullCost;
          const discTag = m.periods.map((p) => `${p.discountPct === 100 ? "GRATIS" : `-${p.discountPct}%`}${p.months ? ` eerste ${p.months} mnd` : ""}`).join(", ");
          return `
          <tr>
            <td>${m.label}${hasDisc ? ` <span class="trial-tag">${discTag}</span>` : ""}</td>
            <td style="text-align:center;color:var(--slate);font-size:10px">${rateDetails || "-"}</td>
            <td>${hasDisc ? `<span style="text-decoration:line-through;color:var(--slate)">\u20AC${Math.round(m.fullCost).toLocaleString("nl-BE")}</span> \u2192 <span style="color:var(--amber);font-weight:600">${fp.discountPct === 100 ? "GRATIS" : `\u20AC${Math.round(currentCost).toLocaleString("nl-BE")}`}</span>` : `\u20AC${Math.round(m.fullCost).toLocaleString("nl-BE")}`}</td>
          </tr>`;
        }).join("")}
        ${priceBreakdown.extraUserCost > 0 ? `<tr><td>Extra gebruikers</td><td style="text-align:center;color:var(--slate);font-size:10px">${extraUsers} \u00D7 \u20AC${tierPricing[tier]?.pricePerExtraUser || 0}</td><td>\u20AC${Math.round(priceBreakdown.extraUserCost).toLocaleString("nl-BE")}</td></tr>` : ""}
        <tr class="subtotal"><td>Maandelijks totaal (na kortingen)</td><td></td><td class="amt">\u20AC${Math.round(priceBreakdown.pricingTimeline[0]?.mrrPrice || grossMRR).toLocaleString("nl-BE")}<span style="font-size:11px;font-weight:400;color:var(--slate)"> /mnd</span></td></tr>
        ${(priceBreakdown.pricingTimeline[0]?.discountPerMonth || 0) > 1 ? `<tr><td colspan="2" style="border-bottom:none;font-size:10px;color:var(--slate)">Volledige prijs na kortingsperiode</td><td style="border-bottom:none;font-size:10px;color:var(--slate)">\u20AC${Math.round(grossMRR).toLocaleString("nl-BE")} /mnd</td></tr>` : ""}
      </tbody>
    </table>
  </div>

  <!-- 3. Wat betaalt u effectief — date-range timeline -->
  <div class="sec">
    <div class="sec-t">Wat betaalt u effectief?</div>
    <table>
      <thead><tr><th>Periode</th><th style="text-align:right">Prijs / maand</th><th style="text-align:right">Korting / maand</th></tr></thead>
      <tbody>
        ${priceBreakdown.pricingTimeline.map((seg) => {
          const isLast = seg.endMonth === null;
          const hasDiscount = seg.discountPerMonth > 1;
          const periodLabel = isLast
            ? `Na ${seg.startDate} (volledige prijs)`
            : `${seg.startDate} \u2014 ${seg.endDate} (${seg.months} mnd)`;
          const details: string[] = [];
          if (seg.activeTrials.length > 0) details.push(`Gratis: ${seg.activeTrials.join(", ")}`);
          if (seg.yearlyDiscountPct > 0) details.push(`-${seg.yearlyDiscountPct}% korting`);
          return `
          <tr${isLast ? ` style="background:var(--light);font-weight:600"` : ""}>
            <td>${periodLabel}${details.length > 0 ? `<div style="font-size:9px;color:var(--amber);font-weight:400;margin-top:1px">${details.join(" &middot; ")}</div>` : ""}</td>
            <td style="text-align:right;font-weight:600">\u20AC${Math.round(seg.mrrPrice).toLocaleString("nl-BE")}/mnd</td>
            <td style="text-align:right;color:var(--green)">${hasDiscount && !isLast ? `-\u20AC${Math.round(seg.discountPerMonth).toLocaleString("nl-BE")}/mnd` : "\u2014"}</td>
          </tr>`;
        }).join("")}
        ${priceBreakdown.totalDiscountSavings > 1 ? `
        <tr style="background:#ecfdf5">
          <td colspan="2" style="color:#059669;font-weight:600;border-bottom:none">Totaal besparing kortingsperiode</td>
          <td style="text-align:right;color:#059669;font-weight:700;border-bottom:none">-\u20AC${Math.round(priceBreakdown.totalDiscountSavings).toLocaleString("nl-BE")}</td>
        </tr>` : ""}
      </tbody>
    </table>
  </div>

  ${oneOffFee > 0 ? `
  <div class="sec">
    <table>
      <tbody>
        <tr>
          <td style="color:var(--slate)">Eenmalige opstartkost <span style="font-size:10px;font-weight:400">\u2014 setup, configuratie en onboarding</span></td>
          <td style="text-align:right;font-weight:700;font-size:14px">\u20AC${oneOffFee.toLocaleString("nl-BE")}</td>
        </tr>
      </tbody>
    </table>
  </div>` : ""}

  ${notes ? `<div class="notes"><b>Opmerkingen:</b> ${notes}</div>` : ""}

  <!-- Signature block -->
  <div style="margin-top:32px;page-break-inside:avoid">
    <p style="font-size:12px;font-weight:600;margin-bottom:4px">Voor akkoord, gelieve getekend terug te bezorgen voor ${validUntilFmt}.</p>
    <div style="display:flex;gap:40px;margin-top:20px">
      <div style="flex:1">
        <p style="font-size:10px;color:var(--slate);margin-bottom:4px">Namens Yally</p>
        <div style="border-bottom:1px solid #0f172a;height:60px"></div>
        <p style="font-size:9px;color:var(--slate);margin-top:4px">Naam &amp; handtekening</p>
      </div>
      <div style="flex:1">
        <p style="font-size:10px;color:var(--slate);margin-bottom:4px">Namens ${customerName}</p>
        <div style="border-bottom:1px solid #0f172a;height:60px"></div>
        <p style="font-size:9px;color:var(--slate);margin-top:4px">Naam &amp; handtekening</p>
      </div>
    </div>
    <div style="display:flex;gap:40px;margin-top:12px">
      <div style="flex:1">
        <p style="font-size:9px;color:var(--slate)">Datum: ____________________</p>
      </div>
      <div style="flex:1">
        <p style="font-size:9px;color:var(--slate)">Datum: ____________________</p>
      </div>
    </div>
  </div>

  <div class="ftr">
    <span>Ref: ${offerReference} \u00B7 Geldig tot ${validUntilFmt}</span>
    <span>Yally \u00B7 yally.app</span>
  </div>

  <div style="margin-top:20px;padding:16px 18px;background:var(--light);border:1px solid var(--border);border-radius:8px;font-size:9px;color:var(--slate);line-height:1.6">
    <p style="margin-bottom:6px">This proposal is subject to our General Terms &amp; Conditions and its Annexes, which are available at: <a href="https://docs.yally.app" style="color:var(--pri)">docs.yally.app</a> and <a href="https://docs.yally.app/legal-and-compliance/legal-and-compliance/terms-and-conditions" style="color:var(--pri)">Terms &amp; Conditions</a>. By accepting this proposal, the Client confirms that they have read, understood, and agreed to these General Terms &amp; Conditions and Annexes.</p>
    <p style="margin-bottom:6px">All fees, including subscription fees, are non-refundable. All sales are final.</p>
    <p>Any country-specific taxes, surcharges, withholding taxes, or other similar deductions imposed by local authorities shall be borne entirely by the Client. All prices are exclusive of VAT.</p>
  </div>

</div>

<!-- ANNEX: Pricing detail -->
<div class="page" style="page-break-before:always">
  <div class="hdr">
    <div><div class="logo">Yally</div></div>
    <div class="hdr-r"><b>Annex \u2014 Prijsdetail</b><br>${offerReference}</div>
  </div>

  <p style="font-size:11px;color:var(--slate);margin-bottom:20px">
    Dit overzicht toont de prijsopbouw per module en per vastgoedcategorie. Bij groei van uw portefeuille worden onderstaande tarieven toegepast.
  </p>

  <!-- Module pricing table -->
  <div class="sec">
    <div class="sec-t">Moduleprijzen per eenheid \u2014 voor uw gekozen modules</div>
    <p style="font-size:10px;color:var(--slate);margin-bottom:6px">Door ons modulesysteem bieden we u de vrijheid om modules te kiezen of te schrappen waar nodig, zodat u alleen betaalt voor wat u werkelijk nodig heeft.</p>
    <table>
      <thead><tr><th>Module</th><th style="text-align:right">Per unit /mnd</th><th style="text-align:right">Per m\u00B2 /mnd</th></tr></thead>
      <tbody>
        ${orderedSelected.map((mk) => {
          const m = modulePricing[mk];
          if (!m) return "";
          const pu = m.pricePerUnit || 0;
          const pm = m.pricePerM2 || 0;
          return `<tr><td>${m.label}</td><td style="text-align:right">${pu === 0 ? `<span style="color:var(--green)">Inbegrepen</span>` : `\u20AC${pu.toFixed(2)}`}</td><td style="text-align:right">${pm === 0 ? `<span style="color:var(--green)">Inbegrepen</span>` : `\u20AC${pm.toFixed(3)}`}</td></tr>`;
        }).join("")}
        ${(() => {
          let tU = 0, tM = 0;
          for (const mk of orderedSelected) { const m = modulePricing[mk]; if (m) { tU += m.pricePerUnit || 0; tM += m.pricePerM2 || 0; } }
          return `<tr style="font-weight:700;border-top:2px solid var(--pri)"><td>Totaal (100%)</td><td style="text-align:right">\u20AC${tU.toFixed(2)}</td><td style="text-align:right">\u20AC${tM.toFixed(3)}</td></tr>`;
        })()}
      </tbody>
    </table>
  </div>

  <!-- Category multipliers table -->
  <div class="sec">
    <div class="sec-t">Prijsfactor per vastgoedcategorie</div>
    <p style="font-size:10px;color:var(--slate);margin-bottom:6px">De basisprijs per module wordt vermenigvuldigd met onderstaande factor per categorie.</p>
    <table>
      <thead><tr><th>Categorie</th><th style="text-align:center">Type</th><th style="text-align:center">Factor</th><th style="text-align:right">Kost per maand /eenheid</th></tr></thead>
      <tbody>
        ${reKeys.map((k) => {
          const info = REAL_ESTATE_LABELS[k];
          const isM2 = rePricingModel[k] === "m2";
          const mult = categoryMultipliers[k] ?? 1;
          const pct = Math.round(mult * 100);
          // Sum effective per-unit price across all selected modules for this category
          let effPrice = 0;
          for (const mk of orderedSelected) {
            const m = modulePricing[mk];
            if (m) effPrice += isM2 ? (m.pricePerM2 || 0) * mult : (m.pricePerUnit || 0) * mult;
          }
          const priceCell = effPrice === 0 ? `<span style="color:var(--green)">Inbegrepen</span>` : `\u20AC${effPrice.toFixed(isM2 ? 3 : 2)} /${isM2 ? "m\u00B2" : "unit"}`;
          return `<tr><td>${info.icon} ${info.label}</td><td style="text-align:center">${isM2 ? "m\u00B2" : "unit"}</td><td style="text-align:center">${pct}%</td><td style="text-align:right">${priceCell}</td></tr>`;
        }).join("")}
      </tbody>
    </table>
  </div>

  <!-- Volume discounts -->
  ${(() => {
    const catsWithDiscounts = reKeys.filter((k) => volumeDiscounts[k]?.length > 0);
    if (catsWithDiscounts.length === 0) return "";
    return `
    <div class="sec">
      <div class="sec-t">Volumekortingen (staffels)</div>
      <p style="font-size:10px;color:var(--slate);margin-bottom:6px">Niet-cumulatief: de hoogste staffel geldt. Korting wordt toegepast op de eenheidsprijs van de betreffende categorie.</p>
      <table>
        <thead><tr><th>Categorie</th><th style="text-align:center">Vanaf</th><th style="text-align:center">Korting</th><th style="text-align:right">Netto prijs /eenheid</th></tr></thead>
        <tbody>
          ${catsWithDiscounts.map((k) => {
            const info = REAL_ESTATE_LABELS[k];
            const isM2 = rePricingModel[k] === "m2";
            const mult = categoryMultipliers[k] ?? 1;
            let baseEffPrice = 0;
            for (const mk of orderedSelected) {
              const m = modulePricing[mk];
              if (m) baseEffPrice += isM2 ? (m.pricePerM2 || 0) * mult : (m.pricePerUnit || 0) * mult;
            }
            return volumeDiscounts[k].map((vd, vi) => {
              const net = baseEffPrice * (1 - vd.discountPct / 100);
              return `<tr><td>${vi === 0 ? `${info.icon} ${info.label}` : ""}</td><td style="text-align:center">${vd.from.toLocaleString("nl-BE")} ${isM2 ? "m\u00B2" : "units"}</td><td style="text-align:center;color:var(--green);font-weight:600">-${vd.discountPct}%</td><td style="text-align:right">\u20AC${net.toFixed(isM2 ? 3 : 2)} /${isM2 ? "m\u00B2" : "unit"}</td></tr>`;
            }).join("");
          }).join("")}
        </tbody>
      </table>
    </div>`;
  })()}

  <div class="ftr">
    <span>Annex bij ${offerReference}</span>
    <span>Yally \u00B7 yally.app</span>
  </div>
</div>

</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };


  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
        {/* Offer form */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">
              {editingOffer ? `Offerte bewerken: ${editingOffer.customerName}` : "Nieuwe klantofferte"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Customer info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Bedrijfsnaam *</label>
                <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Bedrijfsnaam..." />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Contactpersoon</label>
                <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} placeholder="Naam contactpersoon..." />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">E-mail</label>
                <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="contact@..." />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">KBO-nummer</label>
                <Input value={kboNumber} onChange={(e) => setKboNumber(e.target.value)} placeholder="0XXX.XXX.XXX" />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Maatschappelijke zetel</label>
                <Input value={registeredAddress} onChange={(e) => setRegisteredAddress(e.target.value)} placeholder="Straat, nr, postcode, stad" />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Go-live datum</label>
                <Input type="date" value={goLiveDate} onChange={(e) => setGoLiveDate(e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Referentie</label>
                <Input value={offerReference} onChange={(e) => setOfferReference(e.target.value)} placeholder="OFF-..." />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Geldig tot</label>
                <Input type="date" value={validUntilDate} onChange={(e) => setValidUntilDate(e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tier selection */}
            <div>
              <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Startpakket (voorselectie modules)</label>
              <div className="flex flex-wrap gap-2">
                {tierKeys.map((t) => {
                  const tc = tierPricing[t];
                  if (!tc || tc.isFree) return null;
                  return (
                    <button key={t} onClick={() => handleSelectTier(t)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        tier === t ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/40"
                      }`}>
                      {tc.icon} {tc.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Portfolio */}
            <div>
              <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Portefeuille</label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {reKeys.map((k) => {
                  const info = REAL_ESTATE_LABELS[k];
                  const isM2 = rePricingModel[k] === "m2";
                  return (
                    <div key={k}>
                      <span className="text-[9px] text-neutral-secondary-mid">{info.icon} {info.label} ({isM2 ? "m\u00B2" : "units"})</span>
                      <Input type="number" min="0" value={units[k] || ""} onChange={(e) => setUnits({ ...units, [k]: parseInt(e.target.value) || 0 })}
                        placeholder="0" className="h-8 text-xs" />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Extra gebruikers</label>
                <Input type="number" min="0" value={extraUsers || ""} onChange={(e) => setExtraUsers(parseInt(e.target.value) || 0)} className="h-8" />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-secondary-mid block mb-1">Eenmalige kosten</label>
                <Input type="number" min="0" value={oneOffFee || ""} onChange={(e) => setOneOffFee(parseFloat(e.target.value) || 0)} className="h-8" />
              </div>
            </div>

            {/* Yearly discounts */}
            <div>
              <label className="text-xs font-medium text-neutral-secondary-mid block mb-2">Korting per jaar</label>
              <div className="space-y-1.5">
                {yearlyDiscounts.map((yd, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-xs text-neutral-secondary-mid w-12">Jaar {yd.year}</span>
                    <Input type="number" min="0" max="100" value={yd.pct || ""}
                      onChange={(e) => {
                        const next = [...yearlyDiscounts];
                        next[idx] = { ...yd, pct: parseInt(e.target.value) || 0 };
                        setYearlyDiscounts(next);
                      }}
                      className="w-20 h-7 text-xs" />
                    <span className="text-xs text-neutral-secondary-mid">%</span>
                    {yd.pct > 0 && (
                      <span className="text-xs text-emerald-600">= {fmt(priceBreakdown.grossMRR * (1 - yd.pct / 100))} /mnd</span>
                    )}
                    {yearlyDiscounts.length > 1 && (
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive/50 hover:text-destructive"
                        onClick={() => setYearlyDiscounts(yearlyDiscounts.filter((_, i) => i !== idx))}>
                        <Trash size={10} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="text-xs text-primary h-6"
                  onClick={() => setYearlyDiscounts([...yearlyDiscounts, { year: yearlyDiscounts.length + 1, pct: 0 }])}>
                  + Jaar toevoegen
                </Button>
              </div>
            </div>

            <Separator />

            {/* Price breakdown */}
            <div>
              <label className="text-xs font-medium text-neutral-secondary-mid block mb-2">Prijsopbouw (maandelijks na volledige prijs)</label>
              <div className="rounded-lg border p-3 space-y-1 text-xs">
                {/* Internal-only: total per unit and per m² */}
                {(() => {
                  let totalPerUnit = 0;
                  let totalPerM2 = 0;
                  let discPerUnit = 0;
                  let discPerM2 = 0;
                  for (const m of priceBreakdown.moduleBreakdown) {
                    const mod = modulePricing[m.key];
                    if (!mod) continue;
                    totalPerUnit += mod.pricePerUnit || 0;
                    totalPerM2 += mod.pricePerM2 || 0;
                    // Use first active discount period for "now" rate
                    const firstDisc = m.periods.length > 0 ? m.periods[0].discountPct : 0;
                    discPerUnit += (mod.pricePerUnit || 0) * (1 - firstDisc / 100);
                    discPerM2 += (mod.pricePerM2 || 0) * (1 - firstDisc / 100);
                  }
                  const hasDisc = Math.abs(totalPerUnit - discPerUnit) > 0.001 || Math.abs(totalPerM2 - discPerM2) > 0.0001;
                  return (
                    <div className="flex items-center gap-3 pb-1.5 mb-1.5 border-b border-dashed text-[10px] text-neutral-secondary-mid flex-wrap">
                      <span>Totaal /unit: <span className="font-semibold text-foreground">{"\u20AC"}{totalPerUnit.toFixed(2)}</span></span>
                      <span>Totaal /m²: <span className="font-semibold text-foreground">{"\u20AC"}{totalPerM2.toFixed(3)}</span></span>
                      <span className="text-[9px]">(100%, voor multipliers)</span>
                      {hasDisc && (
                        <>
                          <span className="text-[9px]">|</span>
                          <span>Na korting /unit: <span className="font-semibold text-amber-600">{"\u20AC"}{discPerUnit.toFixed(2)}</span></span>
                          <span>Na korting /m²: <span className="font-semibold text-amber-600">{"\u20AC"}{discPerM2.toFixed(3)}</span></span>
                        </>
                      )}
                    </div>
                  );
                })()}
                <div className="flex justify-between text-neutral-secondary-mid">
                  <span>Basisprijs</span>
                  <span>{fmt(priceBreakdown.basePrice)} /mnd</span>
                </div>
                {priceBreakdown.moduleBreakdown.filter((m) => m.fullCost > 0 || m.periods.length > 0).map((m) => {
                  const mod = modulePricing[m.key];
                  // Build per-category rate detail
                  const rateDetails = reKeys
                    .filter((k) => (units[k] || 0) > 0)
                    .map((k) => {
                      const qty = units[k];
                      const isM2 = rePricingModel[k] === "m2";
                      const catMult = categoryMultipliers[k] ?? 1;
                      const rate = isM2 ? (mod?.pricePerM2 || 0) * catMult : (mod?.pricePerUnit || 0) * catMult;
                      return `\u20AC${rate.toFixed(isM2 ? 3 : 2)} \u00D7 ${qty} ${isM2 ? "m\u00B2" : "u"}`;
                    })
                    .join(" + ");
                  const firstPeriod = m.periods[0];
                  const hasDiscount = firstPeriod && firstPeriod.discountPct > 0;
                  const currentCost = hasDiscount ? m.fullCost * (1 - firstPeriod.discountPct / 100) : m.fullCost;
                  const discLabel = m.periods.map((p) => `${p.discountPct}%${p.months ? ` ${p.months}m` : " ∞"}`).join(", ");
                  return (
                    <div key={m.key}>
                      <div className="flex justify-between text-neutral-secondary-mid">
                        <span className="flex items-center gap-1.5">
                          <RenderIcon value={mod?.icon} size={13} />
                          {m.label}
                          {hasDiscount && (
                            <span className="text-amber-600 ml-1">({discLabel})</span>
                          )}
                        </span>
                        <span>
                          {hasDiscount ? (
                            <>
                              <span className="line-through text-neutral-secondary-mid">{fmt(m.fullCost)}</span>
                              <span className="text-neutral-secondary-mid mx-1">&rarr;</span>
                              <span className="text-amber-600 font-medium">{firstPeriod.discountPct === 100 ? "GRATIS" : fmt(currentCost)}</span>
                            </>
                          ) : (
                            fmt(m.fullCost)
                          )} /mnd
                        </span>
                      </div>
                      {rateDetails && (
                        <div className="text-[10px] text-neutral-secondary-mid ml-6 -mt-0.5">{rateDetails}</div>
                      )}
                    </div>
                  );
                })}
                {priceBreakdown.extraUserCost > 0 && (
                  <div className="flex justify-between text-neutral-secondary-mid">
                    <span>Extra gebruikers</span>
                    <span>{fmt(priceBreakdown.extraUserCost)} /mnd</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold pt-1 border-t">
                  <span>Uw maandprijs vandaag</span>
                  <span>{fmt(priceBreakdown.pricingTimeline[0]?.mrrPrice || priceBreakdown.grossMRR)} /mnd</span>
                </div>
                {priceBreakdown.pricingTimeline[0]?.discountPerMonth > 1 && (
                  <div className="flex justify-between text-[10px] text-neutral-secondary-mid">
                    <span>Volledige prijs na kortingsperiode</span>
                    <span>{fmt(priceBreakdown.grossMRR)} /mnd</span>
                  </div>
                )}
              </div>

              {/* Pricing timeline — date-range segments */}
              <label className="text-xs font-medium text-neutral-secondary-mid block mb-2 mt-3">Wat betaalt u effectief?</label>
              <div className="rounded-lg border overflow-hidden text-xs">
                {priceBreakdown.pricingTimeline.map((seg, i) => {
                  const isLast = seg.endMonth === null;
                  const hasDiscount = seg.discountPerMonth > 1;
                  return (
                    <div key={i} className={`flex items-center justify-between px-3 py-2 ${i > 0 ? "border-t" : ""} ${isLast ? "bg-primary/5" : ""}`}>
                      <div>
                        <span className="font-medium">
                          {isLast ? `Na ${seg.startDate}` : `${seg.startDate} — ${seg.endDate}`}
                        </span>
                        <span className="text-neutral-secondary-mid ml-1.5">
                          {isLast ? "(volledige prijs)" : `(${seg.months} mnd)`}
                        </span>
                        {seg.activeTrials.length > 0 && (
                          <span className="text-amber-600 ml-1.5 text-[10px]">
                            gratis: {seg.activeTrials.join(", ")}
                          </span>
                        )}
                        {seg.yearlyDiscountPct > 0 && (
                          <span className="text-emerald-600 ml-1.5 text-[10px]">-{seg.yearlyDiscountPct}% korting</span>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="font-bold">{fmt(seg.mrrPrice)}/mnd</span>
                        {hasDiscount && !isLast && (
                          <span className="text-emerald-600 ml-2">(-{fmt(seg.discountPerMonth)}/mnd)</span>
                        )}
                      </div>
                    </div>
                  );
                })}
                {/* Total discount savings */}
                {priceBreakdown.totalDiscountSavings > 1 && (
                  <div className="flex items-center justify-between px-3 py-2 border-t bg-emerald-50">
                    <span className="font-medium text-emerald-700">Totaal besparing kortingsperiode</span>
                    <span className="font-bold text-emerald-700">-{fmt(priceBreakdown.totalDiscountSavings)}</span>
                  </div>
                )}
                {/* Eenmalige opstartkost — separate from discounts */}
                {oneOffFee > 0 && (
                  <div className="flex items-center justify-between px-3 py-2 border-t">
                    <span className="font-medium">Eenmalige opstartkost <span className="text-neutral-secondary-mid font-normal">— setup, configuratie en onboarding</span></span>
                    <span className="font-bold">{fmt(oneOffFee)} eenmalig</span>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Module selection */}
            <div>
              <label className="text-xs font-medium text-neutral-secondary-mid block mb-2">Modules</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                {allModules.map((modKey) => {
                  const mod = modulePricing[modKey];
                  const isSelected = selectedModules.has(modKey);
                  const override = moduleOverrides.find((o) => o.moduleKey === modKey);
                  return (
                    <div key={modKey} className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs ${isSelected ? "border-primary/30 bg-primary/5" : "border-border"}`}>
                      <Checkbox checked={isSelected} onCheckedChange={() => {
                        const next = new Set(selectedModules);
                        if (next.has(modKey)) { next.delete(modKey); removeModuleOverride(modKey); } else next.add(modKey);
                        setSelectedModules(next);
                      }} />
                      <span className={`flex items-center gap-1 flex-1 min-w-0 ${isSelected ? "font-medium" : "text-neutral-secondary-mid"}`}>
                        <RenderIcon value={mod.icon} size={14} />
                        <span className="truncate">{mod.label}</span>
                        {isSelected && (
                          <span className="text-[9px] text-neutral-secondary-mid font-normal ml-auto mr-1 flex-shrink-0">
                            {"\u20AC"}{(mod.pricePerUnit || 0).toFixed(2)}/u &middot; {"\u20AC"}{(mod.pricePerM2 || 0).toFixed(3)}/m²
                          </span>
                        )}
                      </span>
                      {isSelected && (
                        <button onClick={() => addModuleOverride(modKey)} className="ml-auto text-[8px] text-blue-600 hover:underline flex-shrink-0">Korting</button>
                      )}
                      {override && (
                        <span className="text-[8px] text-amber-600 font-medium flex-shrink-0">
                          {(override.discountPct ?? (override.priceOverride === 0 ? 100 : 0))}%
                          {(override.months ?? override.trialMonths) ? ` (${override.months ?? override.trialMonths}m)` : " ∞"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Module overrides */}
            {moduleOverrides.length > 0 && (
              <>
                <Separator />
                <div>
                  <label className="text-xs font-medium text-neutral-secondary-mid block mb-2">Module kortingen / try-out</label>
                  <div className="space-y-2">
                    {moduleOverrides.map((ov, idx) => {
                      const mod = modulePricing[ov.moduleKey];
                      const pct = ov.discountPct ?? (ov.priceOverride === 0 ? 100 : 0);
                      const dur = ov.months ?? ov.trialMonths;
                      return (
                        <div key={idx} className="flex items-center gap-2 text-xs">
                          <span className="font-medium w-32 truncate flex items-center gap-1"><RenderIcon value={mod?.icon} size={14} /> {mod?.label}</span>
                          <Input type="number" min="0" max="100" step="5" value={pct}
                            onChange={(e) => updateOverrideByIndex(idx, { discountPct: parseInt(e.target.value) || 0 })}
                            className="w-16 h-7 text-xs" />
                          <span className="text-neutral-secondary-mid">%</span>
                          <Input type="number" min="0" value={dur || ""}
                            onChange={(e) => { const v = parseInt(e.target.value); updateOverrideByIndex(idx, { months: v > 0 ? v : undefined }); }}
                            className="w-16 h-7 text-xs" placeholder="∞" />
                          <span className="text-neutral-secondary-mid text-[10px]">{dur ? "mnd" : "permanent"}</span>
                          <Input value={ov.note || ""} onChange={(e) => updateOverrideByIndex(idx, { note: e.target.value })}
                            className="flex-1 h-7 text-xs" placeholder="Opmerking..." />
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive" onClick={() => removeOverrideByIndex(idx)}>
                            <Trash size={12} />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Functionalities per selected module */}
            {(() => {
              const modsWithFuncs = allModules.filter((k) => selectedModules.has(k)).filter(
                (k) => modulePricing[k]?.functionalities?.length
              );
              if (modsWithFuncs.length === 0) return null;
              return (
                <>
                  <Separator />
                  <div>
                    <label className="text-xs font-medium text-neutral-secondary-mid block mb-2">
                      Functionaliteiten per module
                    </label>
                    <div className="space-y-3">
                      {modsWithFuncs.map((modKey) => {
                        const mod = modulePricing[modKey];
                        const funcs = mod.functionalities || [];
                        const excluded = excludedFuncs[modKey] || new Set();
                        return (
                          <div key={modKey} className="rounded-lg border overflow-hidden">
                            <div className="px-4 py-3 bg-muted/30 border-b">
                              <p className="text-sm font-bold flex items-center gap-2">
                                <RenderIcon value={mod.icon} size={18} /> {mod.label}
                              </p>
                            </div>
                            <table className="w-full">
                              <thead>
                                <tr className="border-b bg-muted/20">
                                  <th className="text-left text-[10px] text-neutral-secondary-mid font-semibold uppercase tracking-wider px-4 py-2 w-8"></th>
                                  <th className="text-left text-[10px] text-neutral-secondary-mid font-semibold uppercase tracking-wider py-2">Functionaliteit</th>
                                  <th className="text-right text-[10px] text-neutral-secondary-mid font-semibold uppercase tracking-wider px-4 py-2 w-[160px]">Waarde</th>
                                </tr>
                              </thead>
                              <tbody>
                                {funcs.map((func) => {
                                  const isExcluded = excluded.has(func.id);
                                  const overrideVal = funcValueOverrides[modKey]?.[func.id];
                                  const rawDefault = func.defaultValue || "";
                                  const displayVal = overrideVal !== undefined ? overrideVal : rawDefault;
                                  const textVal = (displayVal === "\u2714" || displayVal === "\u2713" || displayVal === "v" || displayVal === "V") ? "" : displayVal;
                                  const hasText = textVal.trim().length > 0;

                                  return (
                                    <tr key={func.id} className={`border-b last:border-b-0 ${isExcluded ? "hover:bg-muted/10" : "hover:bg-muted/20"}`}>
                                      <td className="px-4 py-2.5">
                                        <button onClick={() => toggleFuncExclusion(modKey, func.id)}
                                          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                            isExcluded ? "border-gray-300 bg-gray-50" : "border-emerald-400 bg-emerald-50"
                                          }`}>
                                          {!isExcluded && (
                                            <svg viewBox="0 0 10 8" className="w-[9px] h-[9px]">
                                              <polyline points="1 4 3.5 7 9 1" fill="none" stroke="#059669" strokeWidth="2" />
                                            </svg>
                                          )}
                                        </button>
                                      </td>
                                      <td className={`py-2.5 text-sm ${isExcluded ? "text-neutral-secondary-mid" : ""}`}>
                                        {func.label}
                                      </td>
                                      <td className="px-4 py-2 text-right">
                                        <div className="flex items-center gap-2 justify-end">
                                          {/* Checkmark only for included items without text */}
                                          {!isExcluded && !hasText && (
                                            <svg viewBox="0 0 10 8" className="w-4 h-4 flex-shrink-0 text-emerald-600">
                                              <polyline points="1 4 3.5 7 9 1" fill="none" stroke="currentColor" strokeWidth="2" />
                                            </svg>
                                          )}
                                          <Input
                                            value={textVal}
                                            onChange={(e) => setFuncValueOverrides((prev) => ({
                                              ...prev, [modKey]: { ...(prev[modKey] || {}), [func.id]: e.target.value },
                                            }))}
                                            placeholder={isExcluded ? "—" : ""}
                                            className={`h-7 text-sm max-w-[120px] text-right ${isExcluded ? "text-neutral-secondary-mid" : ""}`}
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Notes */}
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opmerkingen voor de klant..." rows={2} className="text-xs" />

            {/* Price summary + actions */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
              <div>
                <p className="text-2xl font-bold text-primary" style={{ fontFamily: "'DM Mono', monospace" }}>
                  {fmt(calculatedMRR)} <span className="text-sm font-normal text-neutral-secondary-mid">/mnd</span>
                </p>
                <p className="text-xs text-neutral-secondary-mid">
                  {fmt(priceBreakdown.yearPricing[0]?.totalRevenue || 0)} jaar 1 ({priceBreakdown.monthsInYear1} mnd)
                  {oneOffFee > 0 && <span className="text-amber-600"> + {fmt(oneOffFee)} eenmalig</span>}
                  {(yearlyDiscounts[0]?.pct || 0) > 0 && <span className="text-blue-600"> ({yearlyDiscounts[0].pct}% korting)</span>}
                  <span className="text-neutral-secondary-mid"> &middot; {fmt(priceBreakdown.grossMRR)} na volledige prijs</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleExportPdf} className="gap-1.5">
                  <FilePdf size={14} /> PDF
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5">
                  <FloppyDisk size={14} /> {saving ? "Opslaan..." : editingOffer ? "Bijwerken" : "Offerte opslaan"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Offers list */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs">Klantoffertes</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-1 max-h-[500px] overflow-y-auto">
            {loading ? (
              <p className="text-xs text-neutral-secondary-mid py-4 text-center">Laden...</p>
            ) : offers.length === 0 ? (
              <p className="text-xs text-neutral-secondary-mid py-4 text-center">Nog geen offertes</p>
            ) : (
              offers.map((offer) => {
                const st = STATUS_LABELS[offer.status] || STATUS_LABELS.draft;
                return (
                  <div key={offer.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer group"
                    onClick={() => loadOffer(offer)}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-xs truncate">{offer.customerName}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${st.color}`}>{st.label}</span>
                        <span className="text-[9px] text-neutral-secondary-mid">{fmt(offer.calculatedMRR || 0)}/mnd</span>
                        {(offer as any).offerData?.offerReference && (
                          <span className="text-[9px] text-neutral-secondary-mid">{(offer as any).offerData.offerReference}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-neutral-secondary-mid hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); if (offer.id) deleteOffer(offer.id); }}>
                        <Trash size={12} />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

    </div>
  );
};
